import React, { forwardRef } from 'react';
import Tooltip from './Tooltip';

// Button variant types matching the old component
export type ButtonVariant =
    | 'filled'
    | 'outline'
    /**
     * `outline`'s geometry on a ground of its own. For a button sitting on a tinted or
     * patterned surface, where `outline`'s transparent background lets that surface through
     * and the control stops reading as a button.
     */
    | 'surface'
    /**
     * The violet the assistant is named in elsewhere — for a control that hands work to it, or
     * acts on what it produced. Provenance, not a severity: it says who is being asked, not
     * that anything is wrong.
     */
    | 'assistant'
    | 'destructive'
    | 'success'
    | 'warning'
    | 'error'
    | 'ghost'
    | 'no-style';

export type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large';

export type ButtonRadius = 'md' | 'lg' | 'xl';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant: ButtonVariant;
    size?: ButtonSize;
    radius?: ButtonRadius;
    loading?: boolean;
    disabled?: boolean;
    icon?: React.ReactNode;
    iconPosition?: 'start' | 'end';
    flexGrow?: boolean;
    fill?: boolean;
    tooltip?: string;
    tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
    iconOnly?: boolean;
    children?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLButtonElement & HTMLAnchorElement>;
    /** Renders the button as a real anchor, so a link action is never an <a> nested in a <button>. */
    href?: string;
    target?: React.HTMLAttributeAnchorTarget;
    rel?: string;
}

// Geometry from the Figma component library, which is variant-independent.
// `min-h` not `h`: Tailwind emits `h-*` after `min-h-*`, so a hard height would beat a
// consumer's own `h-*`. Paired with `whitespace-nowrap` (as Figma has it) so a long label
// cannot wrap into a line the height would clip.
const getSizeClasses = (size: ButtonSize) => {
    switch (size) {
        // Figma has no text X-Small button, only the icon-only square — nothing to align to.
        case 'xsmall':
            return 'px-2 py-1 text-xs';
        case 'small':
            return 'min-h-[var(--malachite-control-height-small,28px)] px-[10px] text-[length:var(--malachite-font-size-small,13px)] leading-[16px] whitespace-nowrap';
        case 'medium':
            return 'min-h-[var(--malachite-control-height-medium,32px)] px-[10px] text-[length:var(--malachite-font-size-small,13px)] leading-[16px] whitespace-nowrap';
        case 'large':
            return 'min-h-[var(--malachite-control-height-large,42px)] px-[12px] text-[length:var(--malachite-font-size-15,15px)] leading-[18px] whitespace-nowrap';
        default:
            return 'min-h-[var(--malachite-control-height-large,42px)] px-[12px] text-[length:var(--malachite-font-size-15,15px)] leading-[18px] whitespace-nowrap';
    }
};

/** An icon-only button is a square on the control ladder; without this it collapses to the
 *  glyph plus its border. */
const getIconOnlySizeClasses = (size: ButtonSize): string => {
    switch (size) {
        case 'xsmall':
            return 'size-[20px] shrink-0';
        case 'small':
            return 'size-[28px] shrink-0';
        case 'medium':
            return 'size-[32px] shrink-0';
        default:
            return 'size-[42px] shrink-0';
    }
};

// `rounded-*` reads the malachite radius tokens via `@theme`, so these follow the theme.
// An explicit `radius` prop still wins, via getRadiusClass.
const getDefaultRadiusClass = (size: ButtonSize, iconOnly: boolean): string => {
    switch (size) {
        // Only the square rounds at 8px — text buttons at this size keep their 6px corner.
        case 'xsmall':
            return iconOnly ? 'rounded-lg' : 'rounded-md';
        case 'small':
            return 'rounded-[var(--malachite-radius-10,10px)]';
        case 'medium':
            return 'rounded-xl';
        case 'large':
            return 'rounded-[var(--malachite-radius-xxl,14px)]';
        default:
            return 'rounded-[var(--malachite-radius-xxl,14px)]';
    }
};

// Figma pairs a 6px icon gap with the 13px tiers and 8px with the 15px tier.
const getIconGapClass = (size: ButtonSize, position: 'start' | 'end'): string => {
    const start = position === 'start';
    switch (size) {
        case 'small':
        case 'medium':
            return start ? 'mr-[6px]' : 'ml-[6px]';
        default:
            return start ? 'mr-2' : 'ml-2';
    }
};

const getIconSizeClass = (size: ButtonSize): string => {
    switch (size) {
        case 'xsmall':
            return '[&_svg]:size-[var(--malachite-icon-size-xsmall)]!';
        case 'small':
            return '[&_svg]:size-[var(--malachite-icon-size-small)]!';
        case 'medium':
            return '[&_svg]:size-[var(--malachite-icon-size-medium)]!';
        case 'large':
            return '[&_svg]:size-[var(--malachite-icon-size-large)]!';
        default:
            return '[&_svg]:size-[var(--malachite-icon-size-large)]!';
    }
};

const getRadiusClass = (radius: ButtonRadius): string => {
    switch (radius) {
        case 'md':
            return 'rounded-md';
        case 'lg':
            return 'rounded-lg';
        case 'xl':
            return 'rounded-xl';
        default:
            return 'rounded-xl';
    }
};

// Merged, never replaced: a caller's own `rel` must not drop `_blank`'s noopener/noreferrer.
const resolveRel = (
    rel: string | undefined,
    target: React.HTMLAttributeAnchorTarget | undefined,
): string | undefined => {
    if (target !== '_blank') return rel;

    const tokens = new Set(rel?.split(/\s+/).filter(Boolean));
    tokens.add('noopener');
    tokens.add('noreferrer');
    return [...tokens].join(' ');
};

// Focus ring: a `focus-visible` outline (keyboard only), coloured to match the variant.
// Uses `outline` rather than Tailwind's `ring-*` because ring composes `--tw-ring-*`
// custom properties via box-shadow that rely on document-level @property registration
// and silently don't paint inside a shadow root (same class of issue as transform
// utilities). The classes are full literals so Tailwind's JIT can generate them.
const FOCUS_OUTLINE = {
    neutral: 'focus-visible:[outline:2px_solid_var(--malachite-text-primary)]',
    destructive: 'focus-visible:[outline:2px_solid_var(--malachite-text-destructive)]',
    success: 'focus-visible:[outline:2px_solid_var(--malachite-text-success)]',
    warning: 'focus-visible:[outline:2px_solid_var(--malachite-text-warning)]',
    error: 'focus-visible:[outline:2px_solid_var(--malachite-text-error)]',
    assistant: 'focus-visible:[outline:2px_solid_var(--malachite-assistant-foreground)]',
};

const getVariantClasses = (variant: ButtonVariant, radiusClass: string): string => {
    const baseClasses = `font-medium transition-[color,background-color,border-color,opacity,box-shadow] duration-200 ease-in-out outline-none focus-visible:outline-offset-2 border border-solid ${radiusClass} cursor-pointer`;

    switch (variant) {
        case 'filled':
            return `${baseClasses} ${FOCUS_OUTLINE.neutral} btn-filled shadow-default`;

        case 'outline':
            return `${baseClasses} ${FOCUS_OUTLINE.neutral} btn-outline shadow-default`;

        case 'surface':
            return `${baseClasses} ${FOCUS_OUTLINE.neutral} btn-surface shadow-default`;

        case 'assistant':
            return `${baseClasses} ${FOCUS_OUTLINE.assistant} btn-assistant shadow-default`;

        case 'destructive':
            return `${baseClasses} ${FOCUS_OUTLINE.destructive} btn-destructive`;

        case 'success':
            return `${baseClasses} ${FOCUS_OUTLINE.success} btn-glass-success`;

        case 'warning':
            return `${baseClasses} ${FOCUS_OUTLINE.warning} btn-glass-warning`;

        case 'error':
            return `${baseClasses} ${FOCUS_OUTLINE.error} btn-glass-error`;

        case 'ghost':
            return `${baseClasses} ${FOCUS_OUTLINE.neutral} btn-ghost`;

        case 'no-style':
            return `${baseClasses} ${FOCUS_OUTLINE.neutral} btn-no-style border-none`;

        default:
            return `${baseClasses} ${FOCUS_OUTLINE.neutral} btn-filled shadow-default`;
    }
};

const LoadingSpinner = () => (
    <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
    >
        <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
        />
        <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
    </svg>
);

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
    (
        {
            variant,
            size = 'large',
            radius,
            loading = false,
            disabled = false,
            icon,
            iconPosition = 'start',
            flexGrow = false,
            fill = false,
            tooltip,
            tooltipPlacement = 'top',
            iconOnly = false,
            children,
            className = '',
            href,
            target,
            rel,
            ...props
        },
        ref,
    ) => {
        const radiusClass = radius ? getRadiusClass(radius) : getDefaultRadiusClass(size, iconOnly);
        const isInactive = disabled || loading;
        const baseClasses = [
            'flex items-center justify-center',
            iconOnly ? `p-0 ${getIconOnlySizeClasses(size)}` : getSizeClasses(size),
            getVariantClasses(variant, radiusClass),
            flexGrow ? 'w-full' : '',
            fill ? 'max-w-full' : '',
            isInactive ? 'cursor-not-allowed' : '',
            tooltip && isInactive ? 'pointer-events-none' : '',
            className,
        ]
            .filter(Boolean)
            .join(' ');

        const renderContent = () => {
            if (loading) {
                return (
                    <>
                        <LoadingSpinner />
                        {children}
                    </>
                );
            }

            const iconWrapperClasses = `inline-flex items-center shrink-0 ${getIconSizeClass(size)}`;

            if (icon && iconPosition === 'start') {
                return (
                    <>
                        <span
                            className={`${iconWrapperClasses}${children ? ` ${getIconGapClass(size, 'start')}` : ''}`}
                        >
                            {icon}
                        </span>
                        {children}
                    </>
                );
            }

            if (icon && iconPosition === 'end') {
                return (
                    <>
                        {children}
                        <span
                            className={`${iconWrapperClasses}${children ? ` ${getIconGapClass(size, 'end')}` : ''}`}
                        >
                            {icon}
                        </span>
                    </>
                );
            }

            return children;
        };

        // An anchor cannot be disabled, so an inactive link still renders as a <button>.
        const button =
            href && !isInactive ? (
                <a
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    href={href}
                    target={target}
                    rel={resolveRel(rel, target)}
                    className={baseClasses}
                    {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
                >
                    {renderContent()}
                </a>
            ) : (
                <button
                    ref={ref as React.Ref<HTMLButtonElement>}
                    disabled={isInactive}
                    className={baseClasses}
                    {...props}
                >
                    {renderContent()}
                </button>
            );

        if (tooltip) {
            const trigger = isInactive ? (
                <span className={`inline-flex cursor-not-allowed${flexGrow ? ' w-full' : ''}`}>
                    {button}
                </span>
            ) : (
                button
            );
            return (
                <Tooltip text={tooltip} placement={tooltipPlacement} fill={flexGrow}>
                    {trigger}
                </Tooltip>
            );
        }

        return button;
    },
);

Button.displayName = 'Button';

export default Button;
